import { useEffect, useState } from "react";
import { Megaphone, Loader2, Send, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const BroadcastRow = ({ b, onChanged }) => {
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const running = b.run?.state === "running";

  const sendTest = async () => {
    if (!testTo.includes("@")) return toast.error("Enter a valid email");
    setBusy("test");
    try {
      await api.post(`/admin/broadcasts/${b.key}/test`, { to: testTo });
      toast.success(`Test sent to ${testTo}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    finally { setBusy(""); }
  };

  const sendAll = async () => {
    if (!window.confirm(`Send "${b.name}" to ${b.pending} users now? This cannot be undone.`)) return;
    setBusy("all");
    try {
      await api.post(`/admin/broadcasts/${b.key}/send-all`);
      toast.success("Broadcast started — sending in the background");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start broadcast"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4" data-testid={`broadcast-${b.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold text-white">{b.name}</p>
          <p className="text-xs text-neutral-400">Subject: {b.subject}</p>
          <p className="mt-1 text-xs text-neutral-500" data-testid={`broadcast-${b.key}-stats`}>
            Sent {b.sent} · Failed {b.failed} · Pending {b.pending}
            {b.run && <> · Last run {fmt(b.run.started_at)} — {b.run.state} ({b.run.done}/{b.run.total})</>}
          </p>
        </div>
        <button type="button" onClick={sendAll} disabled={busy || running || b.pending === 0} data-testid={`broadcast-${b.key}-send-all`}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#0C9F97] disabled:opacity-40">
          {busy === "all" || running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {running ? "Sending…" : `Send to all users (${b.pending})`}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test to… (email)"
          className="w-64 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-xs text-white outline-none focus:border-[#0A8B85]"
          data-testid={`broadcast-${b.key}-test-input`} />
        <button type="button" onClick={sendTest} disabled={!!busy} data-testid={`broadcast-${b.key}-test-btn`}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-700 px-3 py-1.5 text-xs font-bold text-neutral-200 hover:border-[#0A8B85] disabled:opacity-40">
          {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />} Send test
        </button>
      </div>
    </div>
  );
};

export const AdminBroadcasts = () => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/broadcasts").then((r) => setRows(r.data.broadcasts)).catch(() => toast.error("Couldn't load broadcasts"));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rows?.some((b) => b.run?.state === "running")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [rows]);

  return (
    <section className="mb-8" data-testid="admin-broadcasts">
      <div className="mb-3 flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-[#E3B84C]" />
        <h2 className="text-base font-extrabold text-white md:text-lg">One-off broadcasts</h2>
      </div>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-[#0A8B85]" /> : (
        <div className="space-y-3">{rows.map((b) => <BroadcastRow key={b.key} b={b} onChanged={load} />)}</div>
      )}
    </section>
  );
};
